import { useState } from "react";
import { ButtonControl, DialogControl, IconControl } from "spark-core-dx/components";
import { useDashboardDomainContext } from "../DashboardDomainProvider";
import DashboardCreateDialog from "../components/dialogs/DashboardCreateDialog";
import { Loading } from "./Loading";
import "./DashboardSelect.scss";

const getDashboardTitle = (dashboard) =>
  dashboard?.DraftDetails?.Name ||
  dashboard?.PublishedDetails?.Name ||
  "Untitled Dashboard";

const getDashboardDescription = (dashboard) =>
  dashboard?.DraftDetails?.Description ||
  dashboard?.PublishedDetails?.Description ||
  "No description provided.";

const getDashboardIcon = (dashboard) =>
  dashboard?.DraftDetails?.Icon ||
  dashboard?.PublishedDetails?.Icon ||
  "dashboard";

const DashboardSelect = () => {
  const {
    dashboardLib,
    isDashboardLoading,
    canCreateDashboard,
    canEditDashboard,
    canDeleteDashboard,
    navigateToDashboard,
    editDashboardById,
    deleteDashboardById,
    getDashboardWorkspaceSummary,
  } = useDashboardDomainContext();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingEditDashboard, setPendingEditDashboard] = useState(null);
  const [pendingDeleteDashboard, setPendingDeleteDashboard] = useState(null);

  return (
    <div className="dashboard-select-screen">
      {createOpen ? (
        <DashboardCreateDialog
          isActive={createOpen}
          closeDialog={() => setCreateOpen(false)}
        />
      ) : null}

      {pendingEditDashboard ? (
        <DashboardCreateDialog
          isActive={!!pendingEditDashboard}
          dashboard={pendingEditDashboard}
          onSave={(dashboardDraft) => editDashboardById(pendingEditDashboard?._id, dashboardDraft)}
          closeDialog={() => setPendingEditDashboard(null)}
        />
      ) : null}

      <DialogControl
        className="dashboard-dialog"
        openDialog={!!pendingDeleteDashboard}
        title="Delete Dashboard"
        okText="Delete"
        onOk={async () => {
          const dashboardId = pendingDeleteDashboard?._id;
          setPendingDeleteDashboard(null);
          if (dashboardId) {
            await deleteDashboardById(dashboardId, false);
          }
        }}
        onCancel={() => setPendingDeleteDashboard(null)}
      >
        <div className="dialog-text">
          Please confirm that you want to delete{" "}
          <strong>{getDashboardTitle(pendingDeleteDashboard)}</strong>.
        </div>
      </DialogControl>

      <div className="dashboard-select-header">
        <div>
          <div className="eyebrow">Dashboard Workspace</div>
          <h1>Choose a dashboard</h1>
          <p>Select a dashboard to open its pages in the workspace.</p>
        </div>
        {canCreateDashboard &&  (dashboardLib.dashboardList?.length ?? 0) !== 0  && (
          <ButtonControl
            value="New Dashboard"
            type="okay"
            onClick={() => setCreateOpen(true)}
          />
        )}
      </div>

      {isDashboardLoading ? (
        <div className="dashboard-select-loading">
          <Loading />
        </div>
      ) : (
        <div className="dashboard-select-grid">
          {(dashboardLib.dashboardList ?? []).map((dashboard) => {
            const pages = dashboard?.DraftDetails?.Pages ?? dashboard?.PublishedDetails?.Pages ?? [];
            const firstPage = pages.find((page) => !page?.IsDeleted && page?.IsDefault) ?? pages.find((page) => !page?.IsDeleted) ?? null;
            const activeWorkspaceSummary = getDashboardWorkspaceSummary?.(dashboard, {
              preferPublished: !!dashboard?.State?.IsPublished,
            }) ?? null;

            return (
              <div
                key={dashboard?._id}
                className="dashboard-select-card"
                role="button"
                tabIndex={0}
                onClick={() => navigateToDashboard(dashboard?._id, firstPage?.Key ?? null)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigateToDashboard(dashboard?._id, firstPage?.Key ?? null);
                  }
                }}
              >
                <div className="dashboard-select-card-main">
                  <div className="dashboard-select-card-header">
                    <div className="dashboard-select-card-title-row">
                      <IconControl iconKey={getDashboardIcon(dashboard)} />
                      <div className="dashboard-select-card-title">{getDashboardTitle(dashboard)}</div>
                    </div>
                    <div className="dashboard-state-chip">
                      {dashboard?.State?.IsPublished ? "Published" : "Draft"}
                    </div>
                  </div>
                  <div className="dashboard-select-card-description">{getDashboardDescription(dashboard)}</div>
                  {activeWorkspaceSummary?.label ? (
                    <div className="dashboard-select-card-workspace">
                      {activeWorkspaceSummary.label}
                    </div>
                  ) : null}
                </div>
                <div className="dashboard-select-card-footer">
                  <span>{pages.filter((page) => !page?.IsDeleted).length} page(s)</span>
                  {canEditDashboard || canDeleteDashboard ? (
                    <div className="dashboard-select-card-actions">
                      {canEditDashboard ? (
                        <button
                          className="dashboard-select-action-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setPendingEditDashboard(dashboard);
                          }}
                          type="button"
                        >
                          Edit
                        </button>
                      ) : null}
                      {canDeleteDashboard ? (
                        <button
                          className="dashboard-select-action-button dashboard-select-delete-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setPendingDeleteDashboard(dashboard);
                          }}
                          type="button"
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <span>{dashboard?.State?.IsTemplate ? "Template" : "Dashboard"}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isDashboardLoading && (dashboardLib.dashboardList?.length ?? 0) === 0 && (
        <div className="dashboard-select-empty">
          <IconControl iconKey="dashboard" />
          <div className="title">No dashboards yet</div>
          <div className="description">Create the first dashboard to start organizing pages.</div>
          {canCreateDashboard && (
            <ButtonControl
              value="Create Dashboard"
              type="okay"
              className="create-dashboard-btn"
              onClick={() => setCreateOpen(true)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSelect;
